import { useState } from 'react'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import type { CampoPublico, TipoCampo } from '../../types'

interface DynamicFieldProps {
  campo: CampoPublico
  value: unknown
  onChange: (value: unknown) => void
  provinciaSeleccionada?: string
}

function FieldIcon({ tipo }: { tipo: string }) {
  const iconClass = "w-4 h-4 text-blue-600 mr-2 flex-shrink-0"

  switch (tipo) {
    case 'text':
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
        </svg>
      )
    case 'email':
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
        </svg>
      )
    case 'tel':
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
        </svg>
      )
    case 'number':
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
        </svg>
      )
    case 'url':
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
        </svg>
      )
    case 'textarea':
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      )
    case 'select':
    case 'provincias_ecuador':
    case 'ciudades_ecuador':
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l4-4 4 4m0 6l-4 4-4-4" />
        </svg>
      )
    case 'date':
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      )
    case 'checkbox':
    case 'multi_step_checkbox':
    case 'multi_step_checkbox_select':
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    default:
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
      )
  }
}

function formatTelefonoEcuador(value: string): string {
  let clean = value.replace(/[^0-9+]/g, '')
  if (!clean.startsWith('+')) {
    if (clean.startsWith('0')) clean = clean.substring(1)
    if (clean.startsWith('593')) {
      clean = '+' + clean
    } else if (clean.length > 0) {
      clean = '+593' + clean
    }
  }
  if (clean.length > 13) clean = clean.substring(0, 13)
  return clean
}

function validateTelefono(value: string): boolean {
  if (!value || value.length === 0) return true
  const numbersOnly = value.replace(/[^0-9]/g, '')
  const isValidFormat = value.startsWith('+593') && numbersOnly.length === 12
  const phoneNumber = numbersOnly.substring(3)
  return isValidFormat && phoneNumber.startsWith('9')
}

function validateEmail(email: string): boolean {
  if (!email || email.length === 0) return true
  const atCount = (email.match(/@/g) || []).length
  if (atCount !== 1) return false
  const parts = email.split('@')
  if (parts.length !== 2) return false
  const [localPart, domainPart] = parts
  if (!localPart || !domainPart) return false
  if (!domainPart.includes('.')) return false
  const lastDotIndex = domainPart.lastIndexOf('.')
  if (lastDotIndex === -1 || lastDotIndex === domainPart.length - 1) return false
  return true
}

export function DynamicField({ campo, value, onChange, provinciaSeleccionada }: DynamicFieldProps) {
  const { tipo_campo, etiqueta, placeholder, requerido, configuracion_extra } = campo
  const [telError, setTelError] = useState('')
  const [emailError, setEmailError] = useState('')

  const inputClass = "w-full px-4 py-3 border border-gray-300 rounded-lg text-base transition-all duration-200 bg-white focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-blue-600"
  const errorInputClass = "border-red-500 ring-2 ring-red-200"

  const renderField = () => {
    switch (tipo_campo as TipoCampo) {
      case 'text':
      case 'url':
        return (
          <input
            type={tipo_campo}
            placeholder={placeholder}
            value={(value as string) ?? ''}
            onChange={(e) => onChange(e.target.value)}
            required={requerido}
            className={inputClass}
          />
        )

      case 'email':
        return (
          <div>
            <input
              type="email"
              placeholder={placeholder || 'correo@ejemplo.com'}
              value={(value as string) ?? ''}
              onChange={(e) => {
                const val = e.target.value
                onChange(val)
                if (validateEmail(val.trim())) {
                  setEmailError('')
                }
              }}
              onBlur={(e) => {
                const val = e.target.value.trim()
                if (val.length > 0 && !validateEmail(val)) {
                  setEmailError('Debe seguir el formato: correo@mail.com')
                } else {
                  setEmailError('')
                }
              }}
              required={requerido}
              className={`${inputClass} ${emailError ? errorInputClass : ''}`}
            />
            {emailError && (
              <p className="mt-1 text-sm text-red-600">{emailError}</p>
            )}
          </div>
        )

      case 'number':
        return (
          <input
            type="number"
            placeholder={placeholder}
            value={(value as string) ?? ''}
            onChange={(e) => onChange(e.target.value)}
            required={requerido}
            className={inputClass}
          />
        )

      case 'tel':
        return (
          <div>
            <input
              type="tel"
              placeholder={placeholder || '+593 123456789'}
              value={(value as string) ?? ''}
              onChange={(e) => {
                const formatted = formatTelefonoEcuador(e.target.value)
                onChange(formatted)
                if (validateTelefono(formatted)) {
                  setTelError('')
                }
              }}
              onBlur={() => {
                const val = (value as string) ?? ''
                if (val.length > 0 && !validateTelefono(val)) {
                  setTelError('Debe seguir el formato: +593991234567')
                } else {
                  setTelError('')
                }
              }}
              required={requerido}
              className={`${inputClass} ${telError ? errorInputClass : ''}`}
            />
            {telError && (
              <p className="mt-1 text-sm text-red-600">{telError}</p>
            )}
          </div>
        )

      case 'date':
        return (
          <input
            type="date"
            value={(value as string) ?? ''}
            onChange={(e) => onChange(e.target.value)}
            required={requerido}
            className={inputClass}
          />
        )

      case 'textarea':
        return (
          <textarea
            placeholder={placeholder}
            value={(value as string) ?? ''}
            onChange={(e) => onChange(e.target.value)}
            required={requerido}
            rows={4}
            className={`${inputClass} resize-none`}
          />
        )

      case 'select': {
        const opciones = campo.opciones ?? (configuracion_extra?.opciones as string[] | undefined) ?? []
        return (
          <Select value={(value as string) ?? ''} onValueChange={onChange}>
            <SelectTrigger className="w-full px-4 py-3 h-auto border border-gray-300 rounded-lg">
              <SelectValue placeholder={placeholder || 'Selecciona una opción'} />
            </SelectTrigger>
            <SelectContent>
              {opciones.map((op) => (
                <SelectItem key={op} value={op}>{op}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )
      }

      case 'provincias_ecuador': {
        const provincias = campo.opciones ?? []
        return (
          <Select value={(value as string) ?? ''} onValueChange={onChange}>
            <SelectTrigger className="w-full px-4 py-3 h-auto border border-gray-300 rounded-lg">
              <SelectValue placeholder="Selecciona provincia" />
            </SelectTrigger>
            <SelectContent>
              {provincias.map((p) => (
                <SelectItem key={p} value={p}>{p}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )
      }

      case 'ciudades_ecuador': {
        const ciudades = provinciaSeleccionada && campo.ciudades_por_provincia
          ? campo.ciudades_por_provincia[provinciaSeleccionada] ?? []
          : []
        return (
          <Select
            value={(value as string) ?? ''}
            onValueChange={onChange}
            disabled={!provinciaSeleccionada}
          >
            <SelectTrigger className="w-full px-4 py-3 h-auto border border-gray-300 rounded-lg">
              <SelectValue placeholder={provinciaSeleccionada ? 'Selecciona ciudad' : 'Selecciona primero una provincia'} />
            </SelectTrigger>
            <SelectContent>
              {ciudades.map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )
      }

      case 'checkbox': {
        const imagenCheckbox = (configuracion_extra?.imagen_checkbox as string) ?? ''
        const textoCheckbox = (configuracion_extra?.texto_checkbox as string) ?? ''
        const urlTerminos = (configuracion_extra?.url_terminos as string) ?? ''
        return (
          <div className="space-y-4">
            {imagenCheckbox && (
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <img
                  src={imagenCheckbox}
                  alt={etiqueta}
                  className="mx-auto block rounded-lg shadow-sm max-w-full"
                  style={{ maxHeight: '430px', objectFit: 'contain' }}
                  onError={(e) => { e.currentTarget.style.display = 'none' }}
                />
              </div>
            )}
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <label htmlFor={campo.nombre_campo} className="flex items-start space-x-3 cursor-pointer">
                <Checkbox
                  id={campo.nombre_campo}
                  checked={!!value}
                  onCheckedChange={onChange}
                  className="mt-1"
                />
                <span className="text-sm text-blue-700 leading-5">
                  {textoCheckbox || etiqueta}
                  {urlTerminos && (
                    <>
                      {' '}
                      <a href={urlTerminos} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">
                        (Ver términos)
                      </a>
                    </>
                  )}
                </span>
              </label>
            </div>
          </div>
        )
      }

      case 'multi_step_checkbox': {
        const textoTerminos = (configuracion_extra?.texto_multistep_checkbox as string) ?? (configuracion_extra?.texto_terminos as string) ?? ''
        const urlTerminos = (configuracion_extra?.url_terminos_multistep as string) ?? (configuracion_extra?.url_terminos as string) ?? ''
        const imagenCheckbox = (configuracion_extra?.imagen_multistep_checkbox as string) ?? ''
        const etiquetaTextoCondicional = (configuracion_extra?.etiqueta_texto_condicional as string) ?? 'Proporciona más detalles'
        const placeholderCondicional = (configuracion_extra?.placeholder_texto_condicional as string) ?? ''
        const textoCondicionalRequerido = !!(configuracion_extra?.texto_condicional_requerido)
        const currentVal = value as { checkbox: boolean; text: string } | boolean | undefined
        const isChecked = typeof currentVal === 'boolean' ? currentVal : (currentVal?.checkbox ?? false)
        const textVal = typeof currentVal === 'boolean' ? '' : (currentVal?.text ?? '')

        return (
          <div className="space-y-4">
            {imagenCheckbox && (
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <img
                  src={imagenCheckbox}
                  alt={etiqueta}
                  className="mx-auto block rounded-lg shadow-sm max-w-full"
                  style={{ maxHeight: '430px', objectFit: 'contain' }}
                  onError={(e) => { e.currentTarget.style.display = 'none' }}
                />
              </div>
            )}
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <label htmlFor={campo.nombre_campo} className="flex items-start space-x-3 cursor-pointer">
                <Checkbox
                  id={campo.nombre_campo}
                  checked={isChecked}
                  onCheckedChange={(checked) => {
                    onChange({ checkbox: !!checked, text: checked ? textVal : '' })
                  }}
                  className="mt-1"
                />
                <span className="text-sm text-blue-700 leading-5">
                  {textoTerminos || etiqueta}
                  {urlTerminos && (
                    <>
                      {' '}
                      <a href={urlTerminos} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">
                        (Ver términos)
                      </a>
                    </>
                  )}
                </span>
              </label>
            </div>
            {isChecked && (
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 transition-all duration-300">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {etiquetaTextoCondicional}
                  {textoCondicionalRequerido && <span className="text-red-500 ml-1">*</span>}
                </label>
                <input
                  type="text"
                  value={textVal}
                  placeholder={placeholderCondicional}
                  onChange={(e) => onChange({ checkbox: true, text: e.target.value })}
                  className={inputClass}
                />
              </div>
            )}
          </div>
        )
      }

      case 'multi_step_checkbox_select': {
        const opcionesSelect = (configuracion_extra?.opciones_select_condicional as string[] | undefined) ?? (configuracion_extra?.opciones as string[] | undefined) ?? campo.opciones ?? []
        const textoCheckbox = (configuracion_extra?.texto_multistep_checkbox_select as string) ?? ''
        const urlTerminos = (configuracion_extra?.url_terminos_multistep_select as string) ?? ''
        const imagenCheckbox = (configuracion_extra?.imagen_multistep_checkbox_select as string) ?? ''
        const etiquetaSelect = (configuracion_extra?.etiqueta_select_condicional as string) ?? 'Selecciona una opción'
        const selectRequerido = !!(configuracion_extra?.select_condicional_requerido)
        const currentVal = value as { checked: boolean; seleccion: string } | undefined

        return (
          <div className="space-y-4">
            {imagenCheckbox && (
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <img
                  src={imagenCheckbox}
                  alt={etiqueta}
                  className="mx-auto block rounded-lg shadow-sm max-w-full"
                  style={{ maxHeight: '430px', objectFit: 'contain' }}
                  onError={(e) => { e.currentTarget.style.display = 'none' }}
                />
              </div>
            )}
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <label htmlFor={campo.nombre_campo} className="flex items-start space-x-3 cursor-pointer">
                <Checkbox
                  id={campo.nombre_campo}
                  checked={currentVal?.checked ?? false}
                  onCheckedChange={(checked) => onChange({ ...currentVal, checked, seleccion: checked ? (currentVal?.seleccion ?? '') : '' })}
                  className="mt-1"
                />
                <span className="text-sm text-blue-700 leading-5">
                  {textoCheckbox || etiqueta}
                  {urlTerminos && (
                    <>
                      {' '}
                      <a href={urlTerminos} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">
                        (Ver términos)
                      </a>
                    </>
                  )}
                </span>
              </label>
            </div>
            {currentVal?.checked && opcionesSelect.length > 0 && (
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 transition-all duration-300">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {etiquetaSelect}
                  {selectRequerido && <span className="text-red-500 ml-1">*</span>}
                </label>
                <Select
                  value={currentVal.seleccion ?? ''}
                  onValueChange={(sel) => onChange({ ...currentVal, seleccion: sel })}
                >
                  <SelectTrigger className="w-full px-4 py-3 h-auto border border-gray-300 rounded-lg">
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    {opcionesSelect.map((op) => (
                      <SelectItem key={op} value={op}>{op}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        )
      }

      case 'checkbox_radio': {
        const opciones = campo.opciones ?? (configuracion_extra?.opciones as string[] | undefined) ?? []
        return (
          <RadioGroup value={(value as string) ?? ''} onValueChange={onChange}>
            {opciones.map((op) => (
              <div key={op} className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                <div className="flex items-center gap-2">
                  <RadioGroupItem value={op} id={`${campo.nombre_campo}-${op}`} />
                  <Label htmlFor={`${campo.nombre_campo}-${op}`} className="text-sm font-normal cursor-pointer text-blue-700">
                    {op}
                  </Label>
                </div>
              </div>
            ))}
          </RadioGroup>
        )
      }

      case 'checkbox_multiple': {
        const opciones = campo.opciones ?? (configuracion_extra?.opciones as string[] | undefined) ?? []
        const selected = (value as string[]) ?? []
        return (
          <div className="space-y-2">
            {opciones.map((op) => (
              <div key={op} className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id={`${campo.nombre_campo}-${op}`}
                    checked={selected.includes(op)}
                    onCheckedChange={(checked) => {
                      if (checked) onChange([...selected, op])
                      else onChange(selected.filter((s) => s !== op))
                    }}
                  />
                  <Label htmlFor={`${campo.nombre_campo}-${op}`} className="text-sm font-normal cursor-pointer text-blue-700">
                    {op}
                  </Label>
                </div>
              </div>
            ))}
          </div>
        )
      }

      default:
        return (
          <input
            placeholder={placeholder}
            value={(value as string) ?? ''}
            onChange={(e) => onChange(e.target.value)}
            className={inputClass}
          />
        )
    }
  }

  const isCheckboxType = ['checkbox', 'multi_step_checkbox', 'multi_step_checkbox_select', 'checkbox_radio', 'checkbox_multiple'].includes(tipo_campo)

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-3 sm:p-4">
      {!isCheckboxType && (
        <label className="block text-xs sm:text-sm font-semibold text-gray-900 mb-2 sm:mb-3 flex items-center flex-wrap">
          <FieldIcon tipo={tipo_campo} />
          <span>{etiqueta}</span>
          {requerido && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      {isCheckboxType && (
        <label className="block text-xs sm:text-sm font-semibold text-gray-900 mb-2 sm:mb-3 flex items-center flex-wrap">
          <FieldIcon tipo={tipo_campo} />
          <span>{etiqueta}</span>
          {requerido && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      {renderField()}
    </div>
  )
}
